/**
 * The live reads behind `/analytics`, and the parsing that turns them into `EnquiryFact`s.
 *
 * ---- Why this reads records instead of calling an aggregate method ----
 *
 * `analytics.get_funnel`, `get_workload` and `get_conversion_breakdown` are asks E14-E16; none of
 * them exist on the live server (verified 2026-08-09: HTTP 417, "Failed to get method for
 * command"). Rather than ship a dashboard that shows nothing until they land, the screen reads the
 * underlying records and aggregates them in `src/lib/analytics/aggregate.ts`.
 *
 * ---- Three requests, not an N+1 ----
 *
 *   GET /api/resource/Enquiry           fields=[…] limit_page_length=0        → 23 rows
 *   GET /api/resource/Enquiry Item      parent=Enquiry fields=[parent] …=0    → 159 rows
 *   GET /api/resource/Version           filters=[[ref_doctype,=,Enquiry]] …=0 → 119 rows
 *
 * The child table returns WHOLE in one request once `parent` is supplied. Without that param
 * Frappe answers `PermissionError: Insufficient Permission for Enquiry Item`, which is easy to
 * misread as a permission wall — it is not, it is a missing parameter (see `GatewayQuery.parent`).
 * Counting `Enquiry Item` rows by `parent` reproduces a per-document read exactly
 * (`ENQ-2026-00024` = 130 both ways, measured 2026-08-09).
 *
 * ~50KB total at today's scale. This does not scale to tens of thousands of enquiries, and the
 * fix when it stops scaling is E14-E16, not pagination here.
 *
 * ---- The doctype is `Enquiry` ----
 *
 * Not `Opportunity`, despite `CLAUDE.md`. Measured 2026-08-09: `Enquiry` holds the 23 rows
 * (`ENQ-2026-000NN`) that `get_pipeline_analytics` aggregates; `Opportunity` is a separate,
 * near-empty doctype with 6 `OPP-` rows.
 */

import { useQuery } from '@tanstack/react-query'
import { getResource } from './frappe'
import { num, str } from './wire'
import type { EnquiryFact, StatusTransition } from '../analytics/types'
import type { WireQuotationValueRow } from '../analytics/value'

/* ------------------------------------------------------------------ *
 * Wire shapes
 * ------------------------------------------------------------------ */

/** One `/api/resource/Enquiry` row. Only the fields the aggregators need are requested. */
export interface WireEnquiryRow {
  name: string
  status?: string | null
  customer?: string | null
  customer_name?: string | null
  enquiry_type?: string | null
  purchaser?: string | null
  creation?: string | null
  status_changed_at?: string | null
}

/** One `/api/resource/Enquiry Item` row — only `parent`, because only the COUNT is wanted. */
export interface WireEnquiryItemRow {
  parent: string
}

/**
 * One `/api/resource/Version` row. `data` is a JSON *string* holding `{changed: [[field, old,
 * new], …]}` — Frappe's own audit format, not something we designed.
 */
export interface WireVersionRow {
  docname: string
  data: string
  creation: string
}

const ENQUIRY_FIELDS = [
  'name',
  'status',
  'customer',
  'customer_name',
  'enquiry_type',
  'purchaser',
  'creation',
  'status_changed_at',
]

/** `limit_page_length=0` means "no limit" to Frappe — every row, one request. */
const NO_LIMIT = 0

const ANALYTICS_STALE_MS = 60 * 1000

/* ------------------------------------------------------------------ *
 * Parsing
 * ------------------------------------------------------------------ */

/**
 * Days between two Frappe datetimes, or null when either is missing or unparseable.
 *
 * Null rather than 0 on purpose: an unknown cycle time must not average in as "instant", which
 * would quietly flatter every purchaser with incomplete data.
 */
export function cycleDays(creation: string, statusChangedAt: string): number | null {
  if (!creation || !statusChangedAt) return null
  // Frappe datetimes are site-local `"YYYY-MM-DD HH:mm:ss[.ffffff]"`. Both ends are parsed the
  // same way, so the difference is correct even though neither instant is anchored to a zone.
  const start = new Date(creation.replace(' ', 'T')).getTime()
  const end = new Date(statusChangedAt.replace(' ', 'T')).getTime()
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null
  const days = (end - start) / 86_400_000
  return days < 0 ? null : Number(days.toFixed(2))
}

/** Count `Enquiry Item` rows per parent enquiry. */
export function countLinesByEnquiry(rows: readonly WireEnquiryItemRow[]): Map<string, number> {
  const counts = new Map<string, number>()
  for (const row of rows) {
    const parent = str(row.parent)
    if (!parent) continue
    counts.set(parent, (counts.get(parent) ?? 0) + 1)
  }
  return counts
}

/**
 * Pull the status changes out of Frappe's `Version` rows.
 *
 * A `Version` row records every field that changed in one save, so most of the 119 rows on live
 * are irrelevant (`use_email_body` flipping back and forth). Only `status` entries matter, and
 * only 8 of them exist — which is exactly the history the funnel needs and the enquiry document
 * itself cannot provide.
 *
 * A malformed `data` string is skipped rather than thrown on: one unparseable audit row must not
 * blank the whole dashboard.
 */
export function parseStatusTransitions(rows: readonly WireVersionRow[]): StatusTransition[] {
  const out: StatusTransition[] = []
  for (const row of rows) {
    let changed: unknown
    try {
      changed = (JSON.parse(row.data) as { changed?: unknown }).changed
    } catch {
      continue
    }
    if (!Array.isArray(changed)) continue
    for (const entry of changed) {
      if (!Array.isArray(entry) || entry.length < 3) continue
      const [field, from, to] = entry as [unknown, unknown, unknown]
      if (field !== 'status') continue
      const toStatus = str(to)
      if (!toStatus) continue
      out.push({
        enquiry: str(row.docname),
        from_status: str(from),
        to_status: toStatus,
        changed_at: str(row.creation),
      })
    }
  }
  return out
}

/** Join the enquiry rows to their line counts. */
export function toEnquiryFacts(
  rows: readonly WireEnquiryRow[],
  lineCounts: ReadonlyMap<string, number>,
): EnquiryFact[] {
  return rows.map((row) => {
    const id = str(row.name)
    return {
      id,
      status: str(row.status),
      // IDENTITY, never the display label — `customer_name` is only ever shown, never keyed on.
      customer: str(row.customer),
      customer_name: str(row.customer_name) || str(row.customer),
      enquiry_type: str(row.enquiry_type),
      purchaser: str(row.purchaser),
      // Live has no user directory on this row, so the id doubles as the label until it does.
      purchaser_name: str(row.purchaser),
      // Absent from the map = zero lines, which is a real state: 3 of 23 live enquiries have none.
      lines: num(lineCounts.get(id) ?? 0),
      // Date part only — `"YYYY-MM-DD HH:mm:ss"` sliced, so it compares directly against the
      // range picker's plain date strings without a timezone ever entering the picture.
      created_on: str(row.creation).slice(0, 10),
      cycle_days: cycleDays(str(row.creation), str(row.status_changed_at)),
    }
  })
}

/* ------------------------------------------------------------------ *
 * Hooks
 * ------------------------------------------------------------------ */

/**
 * Every enquiry, joined to its line count and paired with the status history.
 *
 * One hook rather than three, because no board wants a subset: the funnel needs facts AND history,
 * and workload and conversion both need facts. Three separate queries would make every board wait
 * on the slowest of them anyway, with three loading states to reconcile instead of one.
 *
 * NOTE: this deliberately fetches ALL enquiries and filters by date on the client. The
 * `/api/resource` date filter would apply to `creation`, but the funnel's history spans dates
 * outside any window — an enquiry created in June and won in August belongs in an August funnel —
 * so the range is applied where the facts are assembled, not at the fetch.
 */
export function useAnalyticsSources() {
  return useQuery({
    queryKey: ['analytics', 'sources'] as const,
    staleTime: ANALYTICS_STALE_MS,
    queryFn: async () => {
      const [enquiries, items, versions] = await Promise.all([
        getResource<WireEnquiryRow>('Enquiry', { fields: ENQUIRY_FIELDS, limit: NO_LIMIT }),
        getResource<WireEnquiryItemRow>('Enquiry Item', {
          parent: 'Enquiry',
          fields: ['parent'],
          limit: NO_LIMIT,
        }),
        getResource<WireVersionRow>('Version', {
          filters: [['ref_doctype', '=', 'Enquiry']],
          fields: ['docname', 'data', 'creation'],
          limit: NO_LIMIT,
        }),
      ])

      return {
        facts: toEnquiryFacts(enquiries.data, countLinesByEnquiry(items.data)),
        transitions: parseStatusTransitions(versions.data),
      }
    },
  })
}

/* ------------------------------------------------------------------ *
 * Quotation values — the Value board (Akhil, 2026-09-17)
 * ------------------------------------------------------------------ */

const QUOTATION_VALUE_FIELDS = ['name', 'enquiry', 'customer', 'customer_name', 'status', 'transaction_date', 'customer_currency', 'exchange_rate', 'grand_total', 'net_total', 'total_po_cost'] as const

/**
 * Every quotation's money, in one request — what `get_pipeline_analytics` does not carry. One
 * row per quotation; an enquiry with several (revisions are re-issued on the same document, so
 * this is rare) shows each. Same scale caveat as the enquiry read above.
 */
export function useQuotationValues() {
  return useQuery({
    queryKey: ['analytics', 'quotation-values'] as const,
    staleTime: ANALYTICS_STALE_MS,
    queryFn: async () => {
      const page = await getResource<WireQuotationValueRow>('Quotation', {
        fields: [...QUOTATION_VALUE_FIELDS],
        orderBy: 'transaction_date desc',
        limit: NO_LIMIT,
      })
      return page.data
    },
  })
}

/* ------------------------------------------------------------------ *
 * Sourcing behaviour — the purchaser board (Akhil, 2026-09-19)
 * ------------------------------------------------------------------ */

interface WireRfqRow {
  enquiry?: string | null
  supplier?: string | null
}
interface WireSupplierQuotationRow {
  enquiry?: string | null
  supplier?: string | null
}
interface WireComparisonRow {
  name: string
  enquiry?: string | null
}
interface WireAllocationRow {
  parent: string
  supplier?: string | null
  enquiry_row_no?: string | null
}

/**
 * The four flat lists `buildSourcing` reads — every RFQ, supplier quotation, comparison and
 * allocation line, in four requests. Measured 2026-09-19: 103 / 382 / 55 / 187 rows on the bench;
 * `Comparison Allocation` is a child table and lists only with its parent named.
 */
export function useSourcingSources() {
  return useQuery({
    queryKey: ['analytics', 'sourcing'] as const,
    staleTime: ANALYTICS_STALE_MS,
    queryFn: async () => {
      const [rfqs, quotations, comparisons, allocations] = await Promise.all([
        getResource<WireRfqRow>('RFQ', { fields: ['enquiry', 'supplier'], limit: NO_LIMIT }),
        getResource<WireSupplierQuotationRow>('Supplier Quotation', { fields: ['enquiry', 'supplier'], limit: NO_LIMIT }),
        getResource<WireComparisonRow>('Supplier Quote Comparison', { fields: ['name', 'enquiry'], limit: NO_LIMIT }),
        getResource<WireAllocationRow>('Comparison Allocation', {
          parent: 'Supplier Quote Comparison',
          fields: ['parent', 'supplier', 'enquiry_row_no'],
          limit: NO_LIMIT,
        }),
      ])
      const has = (v: unknown): v is string => typeof v === 'string' && v.trim() !== ''
      return {
        rfqs: rfqs.data.filter((r) => has(r.enquiry) && has(r.supplier)).map((r) => ({ enquiry: str(r.enquiry), supplier: str(r.supplier) })),
        quotations: quotations.data.filter((r) => has(r.enquiry) && has(r.supplier)).map((r) => ({ enquiry: str(r.enquiry), supplier: str(r.supplier) })),
        comparisons: comparisons.data.filter((r) => has(r.enquiry)).map((r) => ({ name: r.name, enquiry: str(r.enquiry) })),
        allocations: allocations.data.filter((r) => has(r.supplier)).map((r) => ({ parent: r.parent, supplier: str(r.supplier), enquiry_row_no: str(r.enquiry_row_no) })),
      }
    },
  })
}
